import { Injectable } from '@angular/core';
import { HttpClient, HttpEvent, HttpEventType, HttpRequest } from '@angular/common/http';
import { Observable, Subject } from 'rxjs';
import { map, tap, catchError } from 'rxjs/operators';
import { environment } from '../../environments/environment';

export interface BucketFolder {
  success: boolean;
  bucket: string;
  folders: string[];
}

export interface ProcessedFile {
  page_id: string;
  original_page_url: string;
  watermark_removed_url: string;
  whitespace_cropped_url: string;
  footer_removed_url: string;
  footer_url: string;
  page_number_url: string;
  book_name: string;
  validation: 'Correct' | 'Incorrect';
  reprocessed_url?: string;
  selected_script: string;
  reprocess_count: number;
  created_at: string;
  // Additional properties for bucket file preview
  path?: string;
  folder_path?: string;
  bucket_name?: string;
  size?: number;
  lastModified?: string;
  type?: string;
  preview_url?: string;
}

export interface ProcessFolderResult {
  success: boolean;
  message: string;
  results: {
    [folderName: string]: {
      success_count: number;
      error_count: number;
    }
  };
  summary: {
    total_success: number;
    total_errors: number;
    all_processed: boolean;
  };
  processed_files: ProcessedFile[];
  processed_files_count: number;
  processing_time_minutes: number;
}

export interface ExistingProcessedFilesResponse {
  success: boolean;
  message?: string;
  processed_files: ProcessedFile[];
  total_count: number;
}

export interface WatermarkScript {
  id: string;
  name: string;
  description: string;
}

export interface BucketHierarchyNode {
  id: string;
  name: string;
  path: string;
  type: 'file' | 'folder';
  children?: BucketHierarchyNode[];
  size?: number;
  lastModified?: string;
  parent?: string;
  expanded?: boolean;
}

export interface BucketHierarchyResponse {
  success: boolean;
  hierarchy: BucketHierarchyNode[];
  bucket: string;
  total_files: number;
  total_folders: number;
}

@Injectable({
  providedIn: 'root'
})
export class BucketService {
  private apiBaseUrl = environment.apiBaseUrl;

  constructor(private http: HttpClient) {}

  /**
   * Convert GCS URL to accessible URL through our API proxy
   * @param gcsUrl - The original GCS URL
   * @returns Accessible URL through our gcs-files endpoint
   */
  private convertGcsUrl(gcsUrl: string): string {
    if (!gcsUrl) return '';
    
    // If it's already our API URL, return as is
    if (gcsUrl.includes('/api/gcs-files/')) {
      return gcsUrl;
    }
    
    try {
      // Handle gs:// format
      if (gcsUrl.startsWith('gs://')) {
        const gsMatch = gcsUrl.match(/gs:\/\/([^\/]+)\/(.+)/);
        if (gsMatch) {
          const objectPath = gsMatch[2];
          return `${this.apiBaseUrl}/api/gcs-files/${objectPath}`;
        }
      }
      
      // Handle https://storage.googleapis.com or https://storage.cloud.google.com format
      if (gcsUrl.includes('storage.googleapis.com') || gcsUrl.includes('storage.cloud.google.com')) {
        const urlObj = new URL(gcsUrl);
        const pathParts = urlObj.pathname.split('/').filter(part => part);
        
        if (pathParts.length >= 2) {
          // Remove bucket name (first segment) and join the rest
          const objectPath = pathParts.slice(1).join('/');
          return `${this.apiBaseUrl}/api/gcs-files/${objectPath}`;
        }
      }
    } catch (error) {
      console.error('Error converting GCS URL:', error);
    }
    
    return gcsUrl; // Return original if conversion fails
  }

  /**
   * Process ProcessedFile object to convert all GCS URLs to accessible URLs
   * @param file - The ProcessedFile object
   * @returns ProcessedFile with converted URLs
   */
  private processFileUrls(file: ProcessedFile): ProcessedFile {
    return {
      ...file,
      original_page_url: this.convertGcsUrl(file.original_page_url),
      watermark_removed_url: this.convertGcsUrl(file.watermark_removed_url),
      whitespace_cropped_url: this.convertGcsUrl(file.whitespace_cropped_url),
      footer_removed_url: this.convertGcsUrl(file.footer_removed_url),
      footer_url: this.convertGcsUrl(file.footer_url),
      page_number_url: this.convertGcsUrl(file.page_number_url),
      reprocessed_url: this.convertGcsUrl(file.reprocessed_url || '')
    };
  }

  /**
   * Fetch the list of folders in a bucket
   * @param bucketName - Name of the bucket to fetch folders from
   * @returns Observable with the list of folders
   */
  getFolders(bucketName: string): Observable<BucketFolder> {
    return this.http.get<BucketFolder>(`${this.apiBaseUrl}/folders?bucket_name=${bucketName}`);
  }

  /**
   * Get existing processed files for a folder
   * @param folderName - Name of the folder to get processed files for
   * @returns Observable with the existing processed files
   */
  getProcessedFiles(folderName: string): Observable<ExistingProcessedFilesResponse> {
    return this.http.get<ExistingProcessedFilesResponse>(`${this.apiBaseUrl}/processed-files?folder=${folderName}`)
      .pipe(
        map(response => ({
          ...response,
          processed_files: response.processed_files.map(file => this.processFileUrls(file))
        }))
      );
  }

  /**
   * Process folders in a bucket
   * @param folders - Array of folder names to process
   * @returns Observable with the processing results
   */
  processFolders(folders: string[]): Observable<ProcessFolderResult> {
    return this.http.post<ProcessFolderResult>(`${this.apiBaseUrl}/process-folders`, {
      folders: folders
    }).pipe(
      map(response => ({
        ...response,
        processed_files: response.processed_files.map(file => this.processFileUrls(file))
      }))
    );
  }

  /**
   * Update validation status for a processed file
   * @param pageId - The page ID to update
   * @param validation - The validation status ('Correct' or 'Incorrect')
   * @returns Observable with the update result
   */
  updateValidation(pageId: string, validation: 'Correct' | 'Incorrect'): Observable<any> {
    return this.http.put(`${this.apiBaseUrl}/api/processed-files/${pageId}/validation`, {
      validation
    });
  }

  /**
   * Get available watermark removal scripts
   * @returns Observable with the list of available scripts
   */
  getWatermarkScripts(): Observable<{success: boolean, scripts: WatermarkScript[], note?: string}> {
    const fullUrl = `${this.apiBaseUrl}/api/watermark-scripts`;
    console.log('🌐 BucketService: Making API call to:', fullUrl);
    console.log('🔧 API Base URL from environment:', this.apiBaseUrl);
    
    return this.http.get<{success: boolean, scripts: WatermarkScript[], note?: string}>(fullUrl).pipe(
      tap(response => {
        console.log('📡 BucketService: Raw API response received:', response);
      }),
      catchError(error => {
        console.error('📡 BucketService: API call failed:', error);
        console.error('📡 Failed URL was:', fullUrl);
        throw error;
      })
    );
  }

  /**
   * Reprocess image with selected script
   * @param pageId - The page ID to reprocess
   * @param scriptName - The script to use for reprocessing
   * @returns Observable with the reprocessing result
   */
  reprocessWithScript(pageId: string, scriptName: string): Observable<any> {
    return this.http.post<any>(`${this.apiBaseUrl}/api/processed-files/${pageId}/reprocess`, {
      scriptName
    }).pipe(
      map((response: any) => {
        if (response.success && response.updated_file) {
          return {
            ...response,
            updated_file: this.processFileUrls(response.updated_file)
          };
        }
        return response;
      })
    );
  }

  /**
   * Accept reprocessed result and make it the main result
   * @param pageId - The page ID to accept reprocessed result for
   * @returns Observable with the acceptance result
   */
  acceptReprocessedResult(pageId: string): Observable<any> {
    return this.http.post<any>(`${this.apiBaseUrl}/api/processed-files/${pageId}/accept-reprocessed`, {}).pipe(
      map((response: any) => {
        if (response.success && response.updated_file) {
          return {
            ...response,
            updated_file: this.processFileUrls(response.updated_file)
          };
        }
        return response;
      })
    );
  }

  /**
   * Get the hierarchical structure of files and folders in a bucket
   * @param bucketName - Name of the bucket (optional, defaults to configured bucket)
   * @returns Observable with the hierarchical structure
   */
  getBucketHierarchy(bucketName?: string): Observable<BucketHierarchyResponse> {
    const params = bucketName ? `?bucket_name=${bucketName}` : '';
    return this.http.get<BucketHierarchyResponse>(`${this.apiBaseUrl}/api/bucket/hierarchy${params}`);
  }

  /**
   * Create a new folder in the bucket
   * @param folderPath - The parent folder path where to create the folder
   * @param folderName - Name of the new folder
   * @param bucketName - Name of the bucket (optional)
   * @returns Observable with the creation result
   */
  createFolder(folderPath: string, folderName: string, bucketName?: string): Observable<any> {
    // Combine parent path and folder name to create the complete folder path
    const completeFolderPath = folderPath ? `${folderPath}/${folderName}` : folderName;
    
    return this.http.post(`${this.apiBaseUrl}/api/bucket/folders`, {
      folderPath: completeFolderPath,
      bucketName: bucketName || 'questions-images-bucket'
    });
  }

  /**
   * Upload multiple files to a specific folder in the bucket
   * @param files - Array of files to upload
   * @param targetPath - The target folder path in the bucket
   * @param bucketName - Name of the bucket (optional)
   * @returns Observable with the upload result
   */
  uploadFiles(files: File[], targetPath: string, bucketName?: string): Observable<any> {
    const formData = new FormData();
    
    // Add files to form data
    files.forEach((file, index) => {
      formData.append('files', file);
    });
    
    // Add metadata with correct parameter names expected by Node.js API
    formData.append('targetFolder', targetPath);
    formData.append('bucketName', bucketName || 'questions-images-bucket');

    return this.http.post(`${this.apiBaseUrl}/api/upload/files`, formData);
  }

  /**
   * Delete files or folders from the bucket
   * @param items - Array of item paths to delete
   * @param bucketName - Name of the bucket (optional)
   * @returns Observable with the deletion result
   */
  deleteItems(items: string[], bucketName?: string): Observable<any> {
    return this.http.delete(`${this.apiBaseUrl}/api/bucket/items`, {
      body: {
        itemPaths: items,
        bucketName: bucketName || 'questions-images-bucket'
      }
    });
  }

  /**
   * Download a file from the bucket
   * Uses the existing /api/gcs-files endpoint to fetch the file
   * @param filePath - Path of the file to download
   * @param bucketName - Name of the bucket (optional, not used since we use gcs-files proxy)
   * @returns Downloads the file directly to browser
   */
  downloadFile(filePath: string, bucketName?: string): void {
    // Use the existing gcs-files proxy endpoint
    const downloadUrl = `${this.apiBaseUrl}/api/gcs-files/${filePath}`;
    
    // Fetch the file as a blob and trigger download
    this.http.get(downloadUrl, { responseType: 'blob' }).subscribe({
      next: (blob) => {
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = filePath.split('/').pop() || 'download';
        link.style.display = 'none';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      },
      error: (err) => {
        console.error('Download failed:', err);
      }
    });
  }

  /**
   * Download a folder as a ZIP file from the bucket
   * Uses the /api/bucket/download/folder-native endpoint
   * @param folderPath - Path of the folder to download
   * @param bucketName - Name of the bucket (optional, defaults to questions-images-bucket)
   * @param progressCallback - Optional callback for progress updates (0-100)
   * @returns Observable that emits progress and completes when download is done
   */
  downloadFolder(
    folderPath: string, 
    bucketName: string = 'questions-images-bucket',
    progressCallback?: (progress: number, status: 'preparing' | 'downloading' | 'complete' | 'error') => void
  ): Observable<boolean> {
    const params = new URLSearchParams();
    params.append('folder_path', folderPath);
    params.append('bucket_name', bucketName);

    const downloadUrl = `${this.apiBaseUrl}/api/bucket/download/folder-native?${params.toString()}`;
    
    console.log('📁 Downloading folder:', folderPath, 'URL:', downloadUrl);
    
    // Notify that we're preparing
    if (progressCallback) {
      progressCallback(0, 'preparing');
    }

    return new Observable<boolean>(observer => {
      const request = new HttpRequest<Blob>('GET', downloadUrl, {
        responseType: 'blob',
        reportProgress: true
      });

      this.http.request<Blob>(request).subscribe({
        next: (event: HttpEvent<Blob>) => {
          if (event.type === HttpEventType.DownloadProgress) {
            if (event.total) {
              const progress = Math.round((event.loaded / event.total) * 100);
              if (progressCallback) {
                progressCallback(progress, 'downloading');
              }
            }
          } else if (event.type === HttpEventType.Response) {
            const blob = event.body as Blob;
            if (blob) {
              const url = window.URL.createObjectURL(blob);
              const link = document.createElement('a');
              link.href = url;
              link.download = `${folderPath.split('/').pop() || 'folder'}.zip`;
              link.style.display = 'none';
              document.body.appendChild(link);
              link.click();
              document.body.removeChild(link);
              window.URL.revokeObjectURL(url);
              console.log('✅ Folder download completed:', folderPath);
              if (progressCallback) {
                progressCallback(100, 'complete');
              }
              observer.next(true);
              observer.complete();
            }
          }
        },
        error: (err) => {
          console.error('❌ Folder download failed:', err);
          if (progressCallback) {
            progressCallback(0, 'error');
          }
          observer.error(err);
        }
      });
    });
  }

  /**
   * Get an image as a blob URL for display in img tags
   * This handles authentication properly since it uses HttpClient
   * @param filePath - Full path of the file in GCS (including folder structure)
   * @param bucketName - Bucket name (not used in URL, kept for API compatibility)
   * @returns Observable that emits the blob URL for the image
   */
  getImageBlobUrl(filePath: string, bucketName?: string): Observable<string> {
    // Use the same URL pattern as downloadFile - just the file path, no bucket in URL
    const imageUrl = `${this.apiBaseUrl}/api/gcs-files/${filePath}`;
    
    console.log('🖼️ Loading image from:', imageUrl);
    
    return this.http.get(imageUrl, { responseType: 'blob' }).pipe(
      map((blob: Blob) => {
        return window.URL.createObjectURL(blob);
      }),
      catchError((error) => {
        console.error('Failed to load image:', error);
        throw error;
      })
    );
  }
} 